import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Inter } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NextNprogress from 'nextjs-progressbar';
import { useEffect } from 'react';

import { Box } from '@madeiramadeirabr/layout-web';
import '@madeiramadeirabr/styled-system/styles.css';
import theme from '@madeiramadeirabr/theme';
import '@styled-system/styles.css';

import { useUI } from '@app/common/context';
import SectionTextBanner from '@app/common/VisualBlocks/SectionTextBanner';
import Footer from '@app/layout/Footer';
import FooterSections from '@app/layout/FooterSections';

import { CLASS_NAME } from '@helpers/constants/layout';

import { TSectionHomePage } from 'types/sections/sections.types';
import Navbar from './Navbar';
import Sidebar from './Sidebar';

const inter = Inter({ subsets: ['latin'], fallback: ['sans-serif'] });

const FeatureBar = dynamic(() => import('@app/common/FeatureBar'), { ssr: false });
const ZendeskChatButton = dynamic(() => import('./ChatButton/ZendeskChatButton'), { ssr: false });
const RibbonBanner = dynamic(() => import('@app/common/RibbonBanner'));

export type LayoutProps = AppProps & {
  children: React.ReactNode;
  menu: Record<string, unknown>;
  ribbonBanner: any;
  showRibbonBanner?: boolean;
  hasDarkBackground?: boolean;
  showNav?: boolean;
  showFooter?: boolean;
  showChatButton?: boolean;
  sectionBannerTextId?: string;
  preloadSections?: TSectionHomePage[];
  featureFlagZipcode?: boolean;
};

function Layout({
  children,
  pageProps: {
    menu = null,
    ribbonBanner = { highlightBanner: null },
    backgroundColor = '$background-neutral-default',
    showRibbonBanner = true,
    showNav = true,
    showFooter = true,
    showChatButton = true,
    hasDarkBackground = true,
    footerData,
    header,
    sectionBannerTextId,
    preloadSections = null,
    featureFlagZipcode = false,
  },
}: LayoutProps): JSX.Element {
  const { pathname, query } = useRouter();
  const { setPageView } = useUI();
  const { highlightBanner } = ribbonBanner;

  useEffect(() => {
    setPageView(pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    document.body.classList.add(CLASS_NAME.SCROLL);
  }, []);

  return (
    <Box css={hasDarkBackground && { bg: backgroundColor }} className={inter.className}>
      <Head>
        <meta charSet="utf-8" />
        <meta name="theme-color" content="#FAFAFA" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta
          httpEquiv="Accept-CH"
          content="viewport-width, width, device-memory, dpr, downlink, ect"
        />

        <link rel="apple-touch-icon" href="/assets/apple-touch-icon-152x152.png" />
      </Head>

      <NextNprogress
        color={theme.colors.base.background.brand.secondary.bold}
        options={{ showSpinner: false, easing: 'ease', speed: 500 }}
        height={7}
      />

      {!!sectionBannerTextId && (
        <SectionTextBanner
          sectionId={sectionBannerTextId}
          sectionPreData={preloadSections[`section_text_banner-0`]}
          sectionIndex={0}
        />
      )}

      {showNav && (
        <>
          <Navbar
            sectionBannerTextId={sectionBannerTextId}
            menuData={menu}
            header={header}
            featureFlagZipcode={featureFlagZipcode}
          />
          <Sidebar sideMenuData={menu} />
        </>
      )}

      <Box
        id="control-box-content"
        as="main"
        css={{
          mx: '$none',
          p: '$none',
          width: '100%',
          bg: backgroundColor,
        }}
      >
        {showRibbonBanner && <RibbonBanner highlightBanner={highlightBanner} />}
        {children}
      </Box>

      {showFooter && (
        <>
          <Box
            as="footer"
            css={{
              left: '$none',
              bottom: '$none',
              width: '100%',
              bg: '$background-neutral-subtlest',
              py: '$xlarge',
            }}
          >
            <Footer footerData={footerData} />
          </Box>
          <FooterSections sections={footerData?.sectionFooter?.sections} />
        </>
      )}

      {showChatButton && (
        <>
          <Box id="mm-chat" />
          <ZendeskChatButton pathname={pathname} routerSlug={query.slug} />
        </>
      )}

      <FeatureBar title="Cookies: A MadeiraMadeira utiliza cookies para personalizar conteúdos e melhorar a sua experiência no site. Ao continuar navegando, você concorda com a nossa Política de Privacidade" />
    </Box>
  );
}

export default Layout;
